import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Building2, Mail, Phone, Calendar, 
  Shield, CheckCircle, XCircle, Users, AlertCircle, 
  Loader2, Crown, UserCheck 
} from 'lucide-react';

import AuthContext from '../../../../context/AuthContext';
import superAdminServices from '../../../../services/superAdminServices';

export default function AllUserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    if (id) {
      fetchUserDetails();
    }
  }, [id]);

  const fetchUserDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await superAdminServices.getUserDetails(authTokens.access, id);
      setUserData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
        isActive 
          ? 'bg-green-100 text-green-700' 
          : 'bg-red-100 text-red-700'
      }`}>
        {isActive ? (
          <>
            <CheckCircle className="h-3 w-3 mr-1" />
            Actif
          </>
        ) : (
          <>
            <XCircle className="h-3 w-3 mr-1" />
            Inactif
          </>
        )}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      'Admin': 'bg-purple-100 text-purple-700',
      'RiskManager': 'bg-orange-100 text-orange-700',
      'RiskIdentifier': 'bg-blue-100 text-blue-700',
      'CartoViewer': 'bg-gray-100 text-gray-700'
    };

    const colorClass = roleColors[role.name] || 'bg-gray-100 text-gray-700';

    return (
      <div className={`flex items-center justify-between p-3 rounded-lg ${colorClass.replace('text-', 'bg-').replace('100', '50')} border`}>
        <div className="flex items-center">
          <Shield className="h-4 w-4 mr-2" />
          <div>
            <p className="text-sm font-medium">{role.name}</p>
            {role.description && (
              <p className="text-xs opacity-75">{role.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {role.is_active ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <XCircle className="h-4 w-4 text-red-600" />
          )}
          <span className={`px-2 py-1 rounded text-xs font-medium ${colorClass}`}>
            {role.is_active ? 'Actif' : 'Inactif'}
          </span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Chargement des détails...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="ml-2 text-sm text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="text-center py-12">
          <p className="text-gray-500">Aucune donnée d'utilisateur trouvée</p>
        </div>
      </div>
    );
  }

  const { user, organization, roles } = userData;

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/admin-panel/all-users')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              {user.first_name && user.last_name 
                ? `${user.first_name} ${user.last_name}`
                : user.email
              }
            </h1>
            <p className="text-xs text-gray-500">Détails de l'utilisateur</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* User Information Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <User className="h-5 w-5 text-gray-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900">Informations personnelles</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Nom complet</label>
                  <p className="text-sm text-gray-900">
                    {user.first_name && user.last_name 
                      ? `${user.first_name} ${user.last_name}`
                      : 'Nom non renseigné'
                    }
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Email</label>
                  <div className="flex items-center">
                    <Mail className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-sm text-gray-900">{user.email}</p>
                  </div>
                </div>
                {user.contact && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Contact</label>
                    <div className="flex items-center">
                      <Phone className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-900">{user.contact}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Statut du compte</label>
                  <div className="mt-1">
                    {getStatusBadge(user.is_active)}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Privilèges système</label>
                  <div className="flex space-x-2 mt-1">
                    {user.is_staff && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                        <Crown className="h-3 w-3 mr-1" />
                        Staff
                      </span>
                    )}
                    {user.is_superuser && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                        <Crown className="h-3 w-3 mr-1" />
                        Super Admin
                      </span>
                    )}
                    {!user.is_staff && !user.is_superuser && (
                      <span className="text-xs text-gray-500">Utilisateur standard</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Créé le</label>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-sm text-gray-900">
                      {new Date(user.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Organization Information Card */}
        {organization && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Building2 className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Organisation</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Nom de l'organisation</label>
                    <p className="text-sm text-gray-900">{organization.name}</p>
                  </div>
                  {organization.description && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Description</label>
                      <p className="text-sm text-gray-900">{organization.description}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Statut d'abonnement</label>
                    <div className="mt-1">
                      {getStatusBadge(organization.is_subscribed)}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Organisation créée le</label>
                    <p className="text-sm text-gray-900">
                      {new Date(organization.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Roles Information Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <UserCheck className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Rôles et permissions</h2>
            </div>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <span>{roles.statistics.total_roles} rôle(s) total</span>
              <span className="text-green-600">{roles.statistics.active_roles_count} actif(s)</span>
              <span className="text-red-600">{roles.statistics.inactive_roles_count} inactif(s)</span>
            </div>
          </div>

          {roles.all_roles.length > 0 ? (
            <div className="space-y-3">
              {roles.all_roles.map((role) => (
                <div key={role.id}>
                  {getRoleBadge(role)}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Aucun rôle assigné</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
